import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedAppalto, RedImecoVoce, RedComputoSOA } from '../../../types/red'
import { VOCE_IMECO_LABEL, VOCE_IMECO_ORDER, EUR } from '../../../constants/red'
import { redApi } from '../../../services/api'
import { downloadBlobResponse } from '../../../lib/downloadBlob'

const TabAppalto: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [appalti, setAppalti] = useState<RedAppalto[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [form, setForm] = useState({
    appaltatore: 'IMECO', importo_contratto: '', penale_giornaliera: '',
    data_inizio_prevista: '', data_fine_prevista: '', num_sal_previsti: '3',
  })

  const loadAppalti = () => {
    setLoading(true)
    redApi.appalto.get(immobile.id).then(res => {
      setAppalti(Array.isArray(res.data) ? res.data : [])
      setLoading(false)
    }).catch(() => {
      setAppalti([])
      setLoading(false)
    })
  }

  useEffect(loadAppalti, [immobile.id])

  const creaNuovoAppalto = async () => {
    if (!form.importo_contratto || !form.data_inizio_prevista || !form.data_fine_prevista) {
      toast.error('Importo, data inizio e data fine sono obbligatori')
      return
    }
    try {
      await redApi.appalto.create({
        immobile_id: immobile.id,
        appaltatore: form.appaltatore,
        importo_contratto: parseFloat(form.importo_contratto),
        penale_giornaliera: parseFloat(form.penale_giornaliera || '0'),
        data_inizio_prevista: form.data_inizio_prevista,
        data_fine_prevista: form.data_fine_prevista,
        num_sal_previsti: parseInt(form.num_sal_previsti, 10),
      })
      toast.success('Appalto creato')
      setForm({
        appaltatore: 'IMECO', importo_contratto: '', penale_giornaliera: '',
        data_inizio_prevista: '', data_fine_prevista: '', num_sal_previsti: '3',
      })
      loadAppalti()
      onUpdate()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore creazione appalto')
    }
  }

  if (loading) return <div className="text-gray-400">Caricamento appalti...</div>

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Appalti (Ditte Esecutrici)</h2>
        <button onClick={creaNuovoAppalto}
          className="btn-primary text-sm">
          + Nuovo appalto (nuova ditta)
        </button>
      </div>

      {/* Creation form always visible when no appalti, or collapsible? Keep it simple: always visible at top */}
      {appalti.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm">Nessun appalto creato per questo immobile.</div>
      )}

      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Crea nuovo appalto</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Appaltatore</label>
            <input type="text" className="input" value={form.appaltatore}
              onChange={e => setForm({ ...form, appaltatore: e.target.value })} />
          </div>
          <div>
            <label className="label">Importo contratto (€)</label>
            <input type="number" className="input" value={form.importo_contratto}
              onChange={e => setForm({ ...form, importo_contratto: e.target.value })} />
          </div>
          <div>
            <label className="label">Penale giornaliera (€)</label>
            <input type="number" className="input" value={form.penale_giornaliera}
              onChange={e => setForm({ ...form, penale_giornaliera: e.target.value })} />
          </div>
          <div>
            <label className="label">N° SAL previsti</label>
            <input type="number" className="input" value={form.num_sal_previsti}
              onChange={e => setForm({ ...form, num_sal_previsti: e.target.value })} />
          </div>
          <div>
            <label className="label">Data inizio prevista</label>
            <input type="date" className="input" value={form.data_inizio_prevista}
              onChange={e => setForm({ ...form, data_inizio_prevista: e.target.value })} />
          </div>
          <div>
            <label className="label">Data fine prevista</label>
            <input type="date" className="input" value={form.data_fine_prevista}
              onChange={e => setForm({ ...form, data_fine_prevista: e.target.value })} />
          </div>
        </div>
      </div>

      {appalti.map(app => (
        <AppaltoCard key={app.id} appalto={app}
          expanded={expandedId === app.id}
          onToggle={() => setExpandedId(expandedId === app.id ? null : app.id)}
          onUpdate={() => { loadAppalti(); onUpdate(); }} />
      ))}
    </div>
  )
}

const AppaltoCard: React.FC<{
  appalto: RedAppalto; expanded: boolean; onToggle: () => void; onUpdate: () => void;
}> = ({ appalto, expanded, onToggle, onUpdate }) => {
  const [checklist, setChecklist] = useState<RedImecoVoce[]>([])
  const [soa, setSoa] = useState<RedComputoSOA[]>([])
  const [uploadingContratto, setUploadingContratto] = useState(false)
  const [uploadingFideiussione, setUploadingFideiussione] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    appaltatore: appalto.appaltatore,
    importo_contratto: String(appalto.importo_contratto || ''),
    penale_giornaliera: String(appalto.penale_giornaliera || ''),
    num_sal_previsti: String(appalto.num_sal_previsti || 3),
    data_inizio_prevista: appalto.data_inizio_prevista || '',
    data_fine_prevista: appalto.data_fine_prevista || '',
  })
  const [soaForm, setSoaForm] = useState({ categoria_soa: '', importo: '' })
  const [customCategoria, setCustomCategoria] = useState('')
  const categorieSOA = ['OG1', 'OG2', 'OG3', 'OS1', 'OS2', 'OS3', 'OS9', 'OS30']

  useEffect(() => {
    if (!isEditing) {
      setEditForm({
        appaltatore: appalto.appaltatore,
        importo_contratto: String(appalto.importo_contratto || ''),
        penale_giornaliera: String(appalto.penale_giornaliera || ''),
        num_sal_previsti: String(appalto.num_sal_previsti || 3),
        data_inizio_prevista: appalto.data_inizio_prevista || '',
        data_fine_prevista: appalto.data_fine_prevista || '',
      })
    }
  }, [appalto])

  const load = () => {
    redApi.imeco.listByAppalto(appalto.id).then(r => setChecklist(r.data)).catch(() => setChecklist([]))
    redApi.computoSOA.listByAppalto(appalto.id).then(r => setSoa(r.data)).catch(() => setSoa([]))
  }

  const handleUploadDocumento = async (
    file: File,
    tipologia: string,
    titolo: string,
    field: 'documento_contratto_id' | 'documento_fideiussione_id',
    setUploading: (v: boolean) => void
  ) => {
    setUploading(true)
    try {
      const placeholder = await redApi.documenti.createPlaceholder({
        immobile_id: appalto.immobile_id,
        fase: 'appalto',
        tipologia,
        titolo,
      })
      await redApi.documenti.upload(placeholder.data.id, file)
      await redApi.appalto.update(appalto.id, { [field]: placeholder.data.id })
      toast.success('Documento caricato')
      onUpdate()
    } catch {
      toast.error('Errore caricamento documento')
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadDocumento = async (documentoId: string, titolo: string) => {
    try {
      const res = await redApi.documenti.download(documentoId)
      downloadBlobResponse(res, titolo)
    } catch {
      toast.error('Errore download')
    }
  }

  useEffect(() => { if (expanded) load(); }, [expanded, appalto.id])

  const fatte = checklist.filter(v => v.stato === 'fatto').length
  const totaleVoci = checklist.length || VOCE_IMECO_ORDER.length
  const percentualeCompletamento = totaleVoci > 0 ? Math.round((fatte / totaleVoci) * 100) : 0

  const toggleVoce = async (voce: RedImecoVoce) => {
    try {
      if (voce.stato === 'fatto') {
        await redApi.imeco.update(voce.id, { stato: 'da_fare' })
      } else {
        const data_completamento = new Date().toISOString().split('T')[0]
        const chiedeScadenza = confirm('Questa voce ha una scadenza da tracciare?')
        const data_scadenza = chiedeScadenza ? (prompt('Data scadenza (YYYY-MM-DD):') || undefined) : undefined
        await redApi.imeco.update(voce.id, { stato: 'fatto', data_completamento, data_scadenza })
      }
      load()
    } catch {
      toast.error('Errore aggiornamento voce')
    }
  }

  const handleAddRigaSOA = async () => {
    const categoria_soa = soaForm.categoria_soa === 'altro' ? customCategoria : soaForm.categoria_soa
    if (!categoria_soa || !soaForm.importo) {
      toast.error('Categoria e importo sono obbligatori')
      return
    }
    const importo = parseFloat(soaForm.importo)
    if (isNaN(importo)) {
      toast.error('Inserire un importo valido')
      return
    }
    try {
      await redApi.computoSOA.addRiga(appalto.id, {
        categoria_soa,
        importo,
      })
      toast.success('Riga SOA aggiunta')
      setSoaForm({ categoria_soa: '', importo: '' })
      setCustomCategoria('')
      load()
    } catch {
      toast.error('Errore aggiunta riga')
    }
  }

  const deleteRigaSOA = async (rigaId: string) => {
    if (!confirm('Eliminare questa riga?')) return
    try {
      await redApi.computoSOA.deleteRiga(rigaId)
      load()
    } catch {
      toast.error('Errore eliminazione riga')
    }
  }

  const registraAperturaCantiere = async () => {
    const data_apertura = prompt('Data apertura cantiere Edilconnect (YYYY-MM-DD):')
    if (!data_apertura) return
    try {
      await redApi.appalto.aperturaCantiere(appalto.id, { data_apertura })
      toast.success('Apertura cantiere registrata')
      onUpdate()
    } catch {
      toast.error('Errore registrazione')
    }
  }

  const handleAvviaLavori = async () => {
    const oggi = new Date().toISOString().split('T')[0]
    try {
      await redApi.appalto.update(appalto.id, { data_inizio_effettiva: oggi, stato: 'in_corso' })
      toast.success('Lavori avviati')
      onUpdate()
    } catch {
      toast.error('Errore')
    }
  }

  const handleCompletaLavori = async () => {
    const oggi = new Date().toISOString().split('T')[0]
    try {
      await redApi.appalto.update(appalto.id, { data_fine_effettiva: oggi, stato: 'completato' })
      toast.success('Lavori completati')
      onUpdate()
    } catch {
      toast.error('Errore')
    }
  }

  const handleSaveEdit = async () => {
    const importo_contratto = parseFloat(editForm.importo_contratto || '0')
    const penale_giornaliera = parseFloat(editForm.penale_giornaliera || '0')
    const num_sal_previsti = parseInt(editForm.num_sal_previsti, 10)
    if (isNaN(importo_contratto)) {
      toast.error('Inserire un importo contratto valido')
      return
    }
    if (isNaN(penale_giornaliera)) {
      toast.error('Inserire una penale giornaliera valida')
      return
    }
    if (isNaN(num_sal_previsti)) {
      toast.error('Inserire un numero SAL previsti valido')
      return
    }
    try {
      await redApi.appalto.update(appalto.id, {
        appaltatore: editForm.appaltatore,
        importo_contratto,
        penale_giornaliera,
        num_sal_previsti,
        data_inizio_prevista: editForm.data_inizio_prevista,
        data_fine_prevista: editForm.data_fine_prevista,
      })
      toast.success('Appalto aggiornato')
      setIsEditing(false)
      onUpdate()
    } catch {
      toast.error('Errore aggiornamento appalto')
    }
  }

  const totaleSOA = soa.reduce((s, r) => s + (r.importo || 0), 0)

  return (
    <div className="card overflow-hidden">
      <div onClick={onToggle} className="p-5 cursor-pointer flex items-center justify-between hover:bg-gray-50">
        <div>
          <h3 className="font-semibold text-gray-900">{appalto.appaltatore}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {EUR.format(appalto.importo_contratto || 0)} — {appalto.stato}
            {appalto.penale_maturata && appalto.penale_maturata > 0 && (
              <span className="text-red-600 font-semibold ml-2">Penale: {EUR.format(appalto.penale_maturata)}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-gray-400">Checklist IMECO</div>
            <div className="text-sm font-semibold">{fatte}/{totaleVoci}</div>
          </div>
          <div className="w-16 h-16 relative">
            <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
              <path className="text-gray-100" strokeWidth="3" stroke="currentColor" fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="text-green-500" strokeWidth="3" strokeDasharray={`${percentualeCompletamento}, 100`}
                stroke="currentColor" fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold">{percentualeCompletamento}%</div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-5 bg-gray-50">
          <div className="flex gap-2 flex-wrap">
            {appalto.stato === 'non_avviato' && (
              <button onClick={handleAvviaLavori} className="btn-secondary text-sm">Avvia lavori</button>
            )}
            {appalto.stato === 'in_corso' && (
              <button onClick={handleCompletaLavori} className="btn-secondary text-sm">Completa lavori</button>
            )}
            <button onClick={() => setIsEditing(!isEditing)} className="btn-secondary text-sm">
              {isEditing ? 'Annulla modifica' : 'Modifica'}
            </button>
            <button
              onClick={async () => {
                if (!confirm('Eliminare questo appalto?')) return
                try {
                  await redApi.appalto.delete(appalto.id)
                  toast.success('Appalto eliminato')
                  onUpdate()
                } catch {
                  toast.error('Errore eliminazione')
                }
              }}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Elimina
            </button>
          </div>

          {isEditing && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">Modifica appalto</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-xs">Appaltatore</label>
                  <input className="input text-sm" value={editForm.appaltatore} onChange={e => setEditForm({ ...editForm, appaltatore: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Importo contratto (€)</label>
                  <input className="input text-sm" type="number" step="0.01" value={editForm.importo_contratto} onChange={e => setEditForm({ ...editForm, importo_contratto: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Penale giornaliera (€)</label>
                  <input className="input text-sm" type="number" step="0.01" value={editForm.penale_giornaliera} onChange={e => setEditForm({ ...editForm, penale_giornaliera: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">N° SAL previsti</label>
                  <input className="input text-sm" type="number" value={editForm.num_sal_previsti} onChange={e => setEditForm({ ...editForm, num_sal_previsti: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Data inizio prevista</label>
                  <input className="input text-sm" type="date" value={editForm.data_inizio_prevista} onChange={e => setEditForm({ ...editForm, data_inizio_prevista: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Data fine prevista</label>
                  <input className="input text-sm" type="date" value={editForm.data_fine_prevista} onChange={e => setEditForm({ ...editForm, data_fine_prevista: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} className="btn-primary text-sm">Salva</button>
                <button onClick={() => setIsEditing(false)} className="text-sm text-gray-500">Annulla</button>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Checklist IMECO</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {VOCE_IMECO_ORDER.map(voceKey => {
                const v = checklist.find(c => c.voce === voceKey)
                if (!v) return null
                return (
                  <label key={v.id} className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={v.stato === 'fatto'} onChange={() => toggleVoce(v)} />
                    <span className={v.stato === 'fatto' ? 'text-gray-700' : 'text-gray-500'}>{VOCE_IMECO_LABEL[voceKey]}</span>
                    {v.data_scadenza && (
                      <span className="ml-auto text-xs text-amber-600">
                        scad. {new Date(v.data_scadenza).toLocaleDateString('it-IT')}
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Importo Computo Metrico — Categorie SOA</h4>
            </div>
            <div className="flex gap-2 items-end mb-3">
              <div className="flex-1">
                <label className="label text-xs">Categoria SOA</label>
                <select className="input text-sm" value={soaForm.categoria_soa} onChange={e => setSoaForm({ ...soaForm, categoria_soa: e.target.value })}>
                  <option value="">-- Seleziona --</option>
                  {categorieSOA.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  <option value="altro">Altro</option>
                </select>
              </div>
              {soaForm.categoria_soa === 'altro' && (
                <div className="flex-1">
                  <label className="label text-xs">Altra categoria</label>
                  <input className="input text-sm" value={customCategoria} onChange={e => setCustomCategoria(e.target.value)} placeholder="Es. OG10" />
                </div>
              )}
              <div className="flex-1">
                <label className="label text-xs">Importo (€)</label>
                <input className="input text-sm" type="number" step="0.01" value={soaForm.importo} onChange={e => setSoaForm({ ...soaForm, importo: e.target.value })} placeholder="0.00" />
              </div>
              <button onClick={handleAddRigaSOA} className="btn-primary text-sm py-1.5">Aggiungi</button>
            </div>
            <table className="w-full text-sm bg-white border border-gray-200 rounded-lg overflow-hidden">
              <tbody>
                {soa.map(r => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="py-2 px-3">{r.categoria_soa}</td>
                    <td className="py-2 px-3 text-right">{EUR.format(r.importo)}</td>
                    <td className="py-2 px-3 text-right">
                      <button onClick={() => deleteRigaSOA(r.id)} className="text-red-500 text-xs hover:underline">Elimina</button>
                    </td>
                  </tr>
                ))}
                {soa.length > 0 && (
                  <tr className="font-semibold">
                    <td className="py-2 px-3">TOTALE</td>
                    <td className="py-2 px-3 text-right">{EUR.format(totaleSOA)}</td>
                    <td className="py-2 px-3" />
                  </tr>
                )}
                {soa.length === 0 && (
                  <tr>
                    <td className="py-4 px-3 text-gray-400 text-center" colSpan={3}>Nessuna categoria SOA inserita</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
            <span className="text-sm font-medium text-gray-700">Apertura Cantiere Edilconnect</span>
            {appalto.data_apertura_cantiere_edilconnect ? (
              <span className="text-sm text-green-700 font-semibold">
                {new Date(appalto.data_apertura_cantiere_edilconnect).toLocaleDateString('it-IT')}
              </span>
            ) : (
              <button onClick={registraAperturaCantiere}
                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700">
                Registra apertura
              </button>
            )}
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">Documenti</h4>

            {/* Contratto */}
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-sm font-medium text-gray-700">Contratto di appalto</span>
              {appalto.documento_contratto_id ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDownloadDocumento(appalto.documento_contratto_id!, 'Contratto ' + appalto.appaltatore)} className="text-xs text-blue-600 hover:underline">Scarica</button>
                  <label className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    {uploadingContratto ? 'Caricamento...' : 'Sostituisci'}
                    <input type="file" className="hidden" onChange={e => {
                      if (e.target.files?.[0]) handleUploadDocumento(e.target.files[0], 'contratto_appalto', 'Contratto ' + appalto.appaltatore, 'documento_contratto_id', setUploadingContratto)
                    }} />
                  </label>
                </div>
              ) : (
                <label className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                  {uploadingContratto ? 'Caricamento...' : 'Carica'}
                  <input type="file" className="hidden" onChange={e => {
                    if (e.target.files?.[0]) handleUploadDocumento(e.target.files[0], 'contratto_appalto', 'Contratto ' + appalto.appaltatore, 'documento_contratto_id', setUploadingContratto)
                  }} />
                </label>
              )}
            </div>

            {/* Fideiussione */}
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-sm font-medium text-gray-700">Polizza fideiussione</span>
              {appalto.documento_fideiussione_id ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDownloadDocumento(appalto.documento_fideiussione_id!, 'Fideiussione ' + appalto.appaltatore)} className="text-xs text-blue-600 hover:underline">Scarica</button>
                  <label className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    {uploadingFideiussione ? 'Caricamento...' : 'Sostituisci'}
                    <input type="file" className="hidden" onChange={e => {
                      if (e.target.files?.[0]) handleUploadDocumento(e.target.files[0], 'fideiussione_appalto', 'Fideiussione ' + appalto.appaltatore, 'documento_fideiussione_id', setUploadingFideiussione)
                    }} />
                  </label>
                </div>
              ) : (
                <label className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                  {uploadingFideiussione ? 'Caricamento...' : 'Carica'}
                  <input type="file" className="hidden" onChange={e => {
                    if (e.target.files?.[0]) handleUploadDocumento(e.target.files[0], 'fideiussione_appalto', 'Fideiussione ' + appalto.appaltatore, 'documento_fideiussione_id', setUploadingFideiussione)
                  }} />
                </label>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TabAppalto
